import { fetchWithHeaders, getErrorFromFetchWithHeaders, reportErrorToFaro } from '@sendible/common';
import { useCallback } from 'react';
import { ProfilesUrls } from '../../constants/urls';
import { useUrlWithCredentials } from '../../data-layer/useUrlWithCredentials';

export type ReminderNotificationType = 'Email' | 'Webhook';

export type ReminderNotificationSettings = {
  type: ReminderNotificationType;
  userIds: number[];
  emails: string[];
  webhookUrl: string | null;
};

export type ReminderResponse = {
  id: number;
  name: string;
  notificationSettings: ReminderNotificationSettings;
};

export type ReminderRequest = {
  name: string;
  notificationSettings: ReminderNotificationSettings;
};

const useRemindersModel = () => {
  const getUrlWithCredentials = useUrlWithCredentials();

  function unwrapResult<T>(response: unknown): T {
    if (typeof response === 'object' && response !== null && 'result' in response) {
      return (response as { result: T }).result;
    }

    return response as T;
  }

  const getReminder = useCallback(
    async (accountId: number): Promise<ReminderResponse | null> => {
      const url = `${getUrlWithCredentials(ProfilesUrls.reminders)}&accountId=${accountId}`;

      try {
        const response = await fetchWithHeaders({ method: 'GET', url });

        return unwrapResult<ReminderResponse | null>(response);
      } catch (error: unknown) {
        reportErrorToFaro(error, 'RemindersModel getReminder');

        throw Error(String(getErrorFromFetchWithHeaders(error)));
      }
    },
    [getUrlWithCredentials]
  );

  const createReminder = useCallback(
    async (payload: ReminderRequest): Promise<ReminderResponse> => {
      const url = getUrlWithCredentials(ProfilesUrls.reminders);

      try {
        const response = await fetchWithHeaders({
          method: 'POST',
          url,
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        });

        return unwrapResult<ReminderResponse>(response);
      } catch (error: unknown) {
        reportErrorToFaro(error, 'RemindersModel createReminder');

        throw Error(String(getErrorFromFetchWithHeaders(error)));
      }
    },
    [getUrlWithCredentials]
  );

  const updateReminder = useCallback(
    async (accountId: number, payload: ReminderRequest): Promise<ReminderResponse> => {
      const url = `${getUrlWithCredentials(ProfilesUrls.reminders)}&accountId=${accountId}`;

      try {
        const response = await fetchWithHeaders({
          method: 'PUT',
          url,
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        });

        return unwrapResult<ReminderResponse>(response);
      } catch (error: unknown) {
        reportErrorToFaro(error, 'RemindersModel updateReminder');

        throw Error(String(getErrorFromFetchWithHeaders(error)));
      }
    },
    [getUrlWithCredentials]
  );

  return {
    getReminder,
    createReminder,
    updateReminder,
  };
};

export default useRemindersModel;
